import { useState, useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import InfoIcon from '@mui/icons-material/Info';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import { FormProvider, useForm } from 'react-hook-form';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import TabsOrganize, { TabT } from 'shared/components/TabsOrganize';
import Loader from 'shared/containers/Loader';
import { APIErrorT } from 'shared/models/api';
import { SCHCTemplate } from 'shared/models/SCHCTemplate';
import api from 'utils/api';
import { hasPermissionOnSCHCTemplate, useAuth } from 'utils/hooks/providers/auth-context';

import './SCHCTemplatesManageData.scss';

import SCHCTemplatesManageDataMainSection from './components/SCHCTemplatesManageDataMainSection';
import SCHCTemplatesManageDataRules from './components/SCHCTemplatesManageDataRules';
import SCHCTemplateLibrary from './library';
import SCHCTemplateValidator from './SCHCTemplatesManageData.validator';

function SCHCTemplatesManageData() {
  const [defaultFormValues, setDefaultFormValues] = useState<SCHCTemplate.Form.ValuesT>({
    name: '',
    sCHCTemplateID: 1,
    nonMatchingRule: 1,
    unruly: false,
    legacy: false,
    description: '',
    rules: [],
  });
  /*
    Implement React Hooks Form through methods and FormProvider
    allows to use the same form inside the nested components
                  by calling useFormContext
  */
  const methods = useForm<SCHCTemplate.Form.ValuesT>({
    mode: 'onChange',
    resolver: yupResolver(SCHCTemplateValidator),
    defaultValues: useMemo(() => defaultFormValues, [defaultFormValues]),
  });
  const {
    handleSubmit,
    reset,
    setError,
  } = methods;
  // Need to use deconstruct props to have
  // isEdit condition working from mounted
  const { schcTemplateId } = useParams() as RouterParams;
  const history = useHistory();
  const isEdit = schcTemplateId !== undefined;
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [needRedirecting, setNeedRedirecting] = useState(false);

  const { authState } = useAuth();

  const hasAccessPermission = (schcTemplateOrganization?: string) => {
    if (hasPermissionOnSCHCTemplate(authState, schcTemplateOrganization) === false) {
      toast.error('You do not have sufficient permission to access this page');
      setNeedRedirecting(true);
    }
    return undefined;
  };

  /*
    SCHCTemplateLibrary:
      We are using SCHCTemplateLibrary from './library' in order to standardize the data.
        > FormatAPIToForm
          Turn the data received from the API (SCHCTemplate.API)
          into format that react-hook-form will be able to understand and manage (SCHCTemplate.Form)
        > FormatFormToAPI
          Turn the react-hook-form data format into the API format.
  */

  const loadDataToEdit = () => {
    setIsLoading(true);
    api.SCHCTemplates.getOne(schcTemplateId as string)
      .then((resp: Response) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              setIsLoading(false);
            });
        } else {
          resp.json()
            .then((data: SCHCTemplate.API.BodyT) => {
              // Check access
              hasAccessPermission(data.organization);
              // Format data
              const newValues = new SCHCTemplateLibrary
                .FormatAPIToForm(data)
                .generatedForm as SCHCTemplate.Form.ValuesT;
              setDefaultFormValues(newValues);
              reset(newValues);
              setIsLoading(false);
            });
        }
      });
  };

  useEffect(() => {
    if (isEdit) {
      loadDataToEdit();
    } else {
      hasAccessPermission();
    }
  }, []);

  const onSubmit = (dataForm: SCHCTemplate.Form.ValuesT) => {
    const apiFormat = new SCHCTemplateLibrary.FormatFormToAPI(dataForm).generatedForm;
    setIsSubmitting(true);
    const request = isEdit
      ? api.SCHCTemplates.update(schcTemplateId as string, apiFormat)
      : api.SCHCTemplates.register(apiFormat);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              const message = error?.message;
              if (message === 'SCHC template ID is duplicated with another template, it must be unique per organization') {
                setError('sCHCTemplateID', { message: 'ID already used - Please enter a new one' });
              } else {
                toast.error(message);
              }
              setIsSubmitting(false);
            });
        } else {
          resp.json()
            .then(() => {
              toast.success(`SCHC Template has been ${isEdit ? 'updated' : 'created'}`);
              history.push(`/schc-templates${isEdit === false ? '' : `/${schcTemplateId}`}`);
              setIsSubmitting(false);
            });
        }
      });
  };

  const tabsContent: TabT[] = [
    {
      label: 'Details',
      name: 'details',
      content: <SCHCTemplatesManageDataMainSection />,
      icon: <InfoIcon />,
      iconPosition: 'start',
    },
    {
      label: 'Rules',
      name: 'rules',
      content: <SCHCTemplatesManageDataRules />,
      icon: <PlaylistAddCheckIcon />,
      iconPosition: 'start',
    },
  ];

  if (needRedirecting) {
    const redirectTo = history.location.pathname.substring(0, history.location.pathname.lastIndexOf('/'));
    return <Redirect to={redirectTo} />;
  }
  if (isLoading) {
    return <Loader.Page />;
  }
  return (
    <>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="title__header">
            <h1>SCHC Template · { isEdit ? 'Edit' : 'Create' }</h1>
            <ButtonSubmitData
              isEdit={isEdit}
              isSubmitting={isSubmitting}
            />
          </div>
          <TabsOrganize tabs={tabsContent} />
        </form>
      </FormProvider>
    </>
  );
}

export default SCHCTemplatesManageData;
